/**
 * Lightweight, page-local feedback (success / error toasts).
 *
 * Pairs with `<FeedbackSnackbar />`. Kept deliberately tiny and context-free so
 * each settings screen owns its own transient feedback without a global store.
 */
import { useCallback, useState } from 'react';

export type FeedbackSeverity = 'success' | 'error' | 'info' | 'warning';

export interface Feedback {
  severity: FeedbackSeverity;
  message: string;
  /**
   * Optional secondary caption line.
   * Used by {@link UseFeedback.propagation} to surface an eventual-consistency
   * hint below the primary success message.
   */
  hint?: string;
}

export interface UseFeedback {
  feedback: Feedback | null;
  notify: (severity: FeedbackSeverity, message: string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  /**
   * Show a success message with an additional "propagating to services" hint
   * below it. Use this for control-plane-backed settings (general / tenant /
   * store profile) where downstream consumers update within a few seconds.
   */
  propagation: (message: string) => void;
  clear: () => void;
}

const PROPAGATION_HINT =
  'Change applied — propagating to services. This may take a few seconds to reflect.';

export function useFeedback(): UseFeedback {
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const notify = useCallback((severity: FeedbackSeverity, message: string) => {
    setFeedback({ severity, message });
  }, []);

  const success = useCallback((message: string) => {
    setFeedback({ severity: 'success', message });
  }, []);

  const error = useCallback((message: string) => {
    setFeedback({ severity: 'error', message });
  }, []);

  const propagation = useCallback((message: string) => {
    setFeedback({ severity: 'success', message, hint: PROPAGATION_HINT });
  }, []);

  const clear = useCallback(() => {
    setFeedback(null);
  }, []);

  return { feedback, notify, success, error, propagation, clear };
}
